<template>
    <div>
        <h6 class="text-subtitle-1 font-weight-bold mb-2">Current Admins</h6>
        <v-list density="compact" style="max-height: 220px; overflow: auto;" class="mb-4 border rounded">
            <v-list-item v-for="admin in admins" :key="admin.itsId"
                :title="`${admin.itsId} - ${admin.member?.fullName}`"
                :subtitle="`${memberRank(admin.member)} - ${memberOrganization(admin.member)}`">
                <template v-slot:prepend>
                    <v-avatar size="32" v-if="admin.member?.pictureInUniform">
                        <v-img :src="admin.member.pictureInUniform" />
                    </v-avatar>
                </template>
                <template v-slot:append>
                    <v-btn icon="mdi-delete" size="x-small" color="error" @click="removeAdmin(admin.itsId)" />
                </template>
            </v-list-item>
            <v-list-item v-if="admins.length === 0" title="No admins added yet" />
        </v-list>

        <h6 class="text-subtitle-1 font-weight-bold mb-2">Add by Search</h6>
        <div class="d-flex align-start mb-4">
            <v-autocomplete
                label="Search member (ITS, name, division, rank)"
                v-model="memberSearch.selectedItsIds"
                v-model:search="memberSearch.search"
                :items="memberSearch.data"
                item-title="fullName"
                item-value="itsId"
                multiple
                chips
                closable-chips
                :custom-filter="customFilter"
                @update:search="debouncedGetMembers"
                hide-details
                class="flex-grow-1"
            >
                <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props"
                        :prepend-avatar="item?.raw?.pictureInUniform"
                        :title="`${item?.raw?.itsId} - ${item?.raw?.fullName}`"
                        :subtitle="`${memberOrganization(item?.raw)} - ${memberRank(item?.raw)}`" />
                </template>
            </v-autocomplete>
            <v-btn class="ml-2 mt-1" :disabled="memberSearch.selectedItsIds.length === 0" @click="addSelectedMembers">
                Add
            </v-btn>
        </div>

        <h6 class="text-subtitle-1 font-weight-bold mb-2">Bulk Add by ITS Numbers</h6>
        <v-textarea
            v-model="bulkItsText"
            label="Enter ITS numbers (comma or newline separated)"
            rows="4"
            hide-details
        />
        <div class="d-flex justify-end mt-2">
            <v-btn :disabled="!bulkItsText.trim()" @click="addBulkMembers">Add All</v-btn>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, inject, defineProps, onMounted } from "vue";
import * as atService from '@/services/attendanceService';
import * as memService from '@/services/memberService';
import debounce from 'lodash/debounce';

const props = defineProps({
    attendanceId: { type: Number, required: true },
});

const loader = inject("loader");
const snackbar = inject("snackBar");
const confirmDialog = inject("confirmDialog");

const admins = ref([]);
const bulkItsText = ref("");
const debouncedGetMembers = ref(null);

const memberSearch = reactive({
    search: '',
    data: [],
    selectedItsIds: [],
});

function memberRank(member) {
    if (!member) return '';
    return member.rank?.description || member.rank || '';
}

function memberOrganization(member) {
    if (!member) return '';
    return member.organization?.name || member.organization || '';
}

function customFilter(itemTitle, queryText, item) {
    const q = queryText.toLowerCase();
    const m = item.raw;
    const fullName = (m.fullName || '').toLowerCase();
    const itsId = m.itsId?.toString() || '';
    const rank = memberRank(m).toLowerCase();
    const organization = memberOrganization(m).toLowerCase();

    return fullName.includes(q)
        || itsId.includes(q)
        || rank.includes(q)
        || organization.includes(q);
}

function parseItsIds(text) {
    return [...new Set(
        text.split(/[\s,;\n\r]+/)
            .map(s => s.trim())
            .filter(s => /^\d+$/.test(s))
            .map(Number)
    )];
}

function showAddResult(result) {
    const parts = [];
    if (result.added) parts.push(`${result.added} added`);
    if (result.skipped) parts.push(`${result.skipped} skipped (already admin)`);
    if (result.invalid?.length) parts.push(`${result.invalid.length} invalid ITS (${result.invalid.join(', ')})`);

    if (parts.length)
        snackbar.success(parts.join('. '));
    else
        snackbar.error("No admins were added");
}

async function getData() {
    loader.show();

    const result = await atService.admins_getAllByMasterId(props.attendanceId)
        .catch(ex => snackbar.error("Error", ex));

    if (result)
        admins.value = result;

    loader.hide();
}

async function getMembers() {
    if (!memberSearch.search)
        return;

    const pageContext = {
        search: memberSearch.search,
        pageNumber: 1,
        pageSize: 10,
        organizationId: 0,
        sectionId: 0,
        statusId: 0,
    };

    const result = await memService.getAll(pageContext)
        .catch(ex => snackbar.error(ex));

    if (result)
        memberSearch.data = result.items;
}

async function addItsIds(itsIds) {
    if (!itsIds.length) {
        snackbar.error("No valid ITS numbers to add");
        return;
    }

    loader.show();

    const result = await atService.admins_add(props.attendanceId, { itsIds })
        .catch(ex => snackbar.error(ex));

    loader.hide();

    if (result) {
        showAddResult(result);
        await getData();
    }
}

async function addSelectedMembers() {
    await addItsIds([...memberSearch.selectedItsIds]);
    memberSearch.selectedItsIds = [];
}

async function addBulkMembers() {
    const itsIds = parseItsIds(bulkItsText.value);
    await addItsIds(itsIds);
    bulkItsText.value = "";
}

async function removeAdmin(itsId) {
    const isDelete = await confirmDialog.confirm(
        "Remove Admin",
        `Remove admin with ITS ${itsId}?`,
        "Yes",
        "No",
        true
    );

    if (!isDelete)
        return;

    loader.show();

    await atService.admins_delete(props.attendanceId, itsId)
        .then(() => snackbar.success("Admin removed"))
        .catch(ex => snackbar.error(ex));

    loader.hide();
    await getData();
}

onMounted(async () => {
    debouncedGetMembers.value = debounce(getMembers, 750);
    await getData();
});
</script>
